/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import '@marriott/global-styles/dist/marriot.global.css';
import '../styles/common.scss';
import {
  CustomModelClient,
  getEnvProp,
  getProcessEnvs,
  populatePIdataLayer,
  UserSessionProvider,
  respGridUtil,
  DynamicDCAPageProvider,
} from '@marriott/mi-headless-utils';
import { envVarKeys } from '../_constants/envVarKeys';
import { defineComponentMapping } from '../import-components';
import { canUseDOM } from '@marriott/mi-offers-components';
import path from 'path';
import { setOffersSubDirectoryPrefix, setSessionKey } from '@marriott/mi-offers-components';
import { setSubDirectoryPrefix } from '@marriott/mi-shop-components/utils';
import { useClientEnvVarsStore, usePersistentGlobalStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
path.resolve('./next.config.js');

declare global {
  interface Window {
    jQuery: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
  }
}

if (canUseDOM) window.$ = window.jQuery = require('jquery-slim/dist/jquery.slim.min.js');

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);
if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

const App = function (props) {
  const { Component, pageProps } = props;
  const { model, isSeoDisable, apolloEnvVars, featureFlags, GOOGLE_MAP_API_KEY } = pageProps;
  const { experience_mcom_mbop: MBOPFeatureFlags = {} } = featureFlags?.featureFlags || {};
  const featureFlagLogo = featureFlags?.featureFlagLogo;

  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);
  const envVars = apolloEnvVars ? apolloEnvVars : {};
  envVars['GOOGLE_MAP_API_KEY'] = GOOGLE_MAP_API_KEY;
  envVars['NEXT_PUBLIC_ENABLE_WEBSDK'] =
    getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK) === 'true' && model?.enableWebSDK === 'true';

  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  envVars['NEXT_PUBLIC_ENABLE_DCA_V2'] = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2);
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const respGridModelClone = JSON.parse(JSON.stringify(respGridModel)); //To deep clone the model
  const [pageReady, setPageReady] = useState(false);
  const {
    NEXT_PUBLIC_SUBMIT_OSERP_URL,
    NEXT_PUBLIC_SUBMIT_SEARCH_URL,
    SESSION_GET_CALL_URL_CLIENT,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    NEXT_PUBLIC_PREFIX,
    UXL_IMAGE_CACHE_DOMAIN,
    TARGET_PROPERTY_TOKEN,
    NEXT_PUBLIC_SUBMIT_MBE_URL,
    NEXT_PUBLIC_DAC_LOGO_ENABLE,
    BAIDU_MAP_API_KEY,
  } = getProcessEnvs();

  envVars['NEXT_PUBLIC_DAC_LOGO_ENABLE'] = NEXT_PUBLIC_DAC_LOGO_ENABLE ?? process.env['NEXT_PUBLIC_DAC_LOGO_ENABLE'];

  let subDirectoryContextPath = '';
  if (model?.subDirectoryContextPath) {
    subDirectoryContextPath = model.subDirectoryContextPath ? model.subDirectoryContextPath : '';
    //from shop
    setSubDirectoryPrefix(subDirectoryContextPath);
  }

  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  if (setPageModel) {
    setPageModel(respGridModelClone);
  }

  if (pageProps?.resolvedUrl === '/offers/findOffers') {
    envVars['NEXT_PUBLIC_SUBMIT_SEARCH_URL'] =
      NEXT_PUBLIC_SUBMIT_OSERP_URL ?? process.env['NEXT_PUBLIC_SUBMIT_OSERP_URL'];
  } else if (pageProps?.resolvedUrl?.includes('/offers/special-offers')) {
    envVars['NEXT_PUBLIC_SUBMIT_SEARCH_URL'] = NEXT_PUBLIC_SUBMIT_MBE_URL ?? process.env['NEXT_PUBLIC_SUBMIT_MBE_URL'];
  } else {
    envVars['NEXT_PUBLIC_SUBMIT_SEARCH_URL'] =
      NEXT_PUBLIC_SUBMIT_OSERP_URL ?? process.env['NEXT_PUBLIC_SUBMIT_OSERP_URL'];
  }
  if (pageProps?.currentLocale === 'zh-CN') {
    envVars['UXL_IMAGE_CACHE_DOMAIN'] = UXL_IMAGE_CACHE_DOMAIN ?? process.env?.['UXL_IMAGE_CACHE_DOMAIN'];
  }
  envVars['BAIDU_MAP_API_KEY'] = BAIDU_MAP_API_KEY ?? process.env?.['BAIDU_MAP_API_KEY'];
  envVars['SESSION_GET_CALL_URL_CLIENT'] = SESSION_GET_CALL_URL_CLIENT ?? process.env['SESSION_GET_CALL_URL_CLIENT'];
  envVars['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'] =
    NEXT_PUBLIC_SUBMIT_OSERP_URL ?? process.env['NEXT_PUBLIC_SUBMIT_OSERP_URL'];
  envVars['NEXT_PUBLIC_PREFIX'] = NEXT_PUBLIC_PREFIX ?? process.env['NEXT_PUBLIC_PREFIX'];
  envVars['TARGET_PROPERTY_TOKEN'] = TARGET_PROPERTY_TOKEN ?? process.env['TARGET_PROPERTY_TOKEN'];
  envVars['ACCEPT_LANGUAGE'] = pageProps?.currentLocale;
  setEnvVars(envVars);
  if (model?.subDirectoryContextPath) {
    setOffersSubDirectoryPrefix(model.subDirectoryContextPath);
    setSessionKey('SUBDIRECTORYCONTEXTPATH', model?.subDirectoryContextPath || '');
  }

  useEffect(() => {
    updateGlobalData('sessionData', pageProps?.sessionData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleLoad = () => setPageReady(true);
    if (document.readyState === 'complete') {
      setPageReady(true);
    } else {
      window.addEventListener('load', handleLoad);
    }
    return () => window.removeEventListener('load', handleLoad);
  }, []);
  useEffect(() => {
    if (canUseDOM) {
      const ribbionVisible = document.querySelector('.m-header__points:not(.display-none):not(.d-none)');
      const searchForm = document.querySelector('.document_search_form_container_el_ref');
      const heroForm = document.querySelector('.hero_item.hero_3x1');

      const screenWidth = window.innerWidth;
      if (ribbionVisible && searchForm && heroForm) {
        if (screenWidth >= 576 && screenWidth <= 991) {
          //Medium -Tablet
          searchForm.classList.add('mt-5');
          heroForm.style.marginTop = '83px';
        } else if (screenWidth <= 576) {
          //Small - Mobile
          searchForm.classList.add('mt-5');
        }
      }
    }
  }, [pageReady]);

  if (canUseDOM) {
    populatePIdataLayer();
  }
  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;

  defineComponentMapping(allowedComponents, pageProps, isSeoDisable, MBOPFeatureFlags, featureFlagLogo);

  return (
    <UserSessionProvider pageProps={pageProps} includePiData={true}>
      <DynamicDCAPageProvider Component={Component} pageProps={pageProps} />
    </UserSessionProvider>
  );
};

export default App;
